import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import SharedFilePickerOld from '../../../../shared/shared-file-picker-old';
import SharedFilePicker from '../../../../shared/shared-file-picker';
import { Dispatch, SetStateAction } from 'react';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import { SectionHeader } from '../../../../shared/section-header';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import { useUserSettings } from '../../../../utils/use-user-settings';
import PickerTriggerButton from './picker-trigger-button';
import SelectedFileRow from './selected-file-row';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import FolderOutlined from '@mui/icons-material/FolderOutlined';

interface StepFolderProps {
  setValue: UseFormSetValue<BulkOpsFormValues>;
  control: Control<BulkOpsFormValues>;
  watch: UseFormWatch<BulkOpsFormValues>;
  spreadSheetName: string;
  setSpreadSheetName: Dispatch<SetStateAction<string>>;
  errors: FieldErrors<BulkOpsFormValues>;
  isNewUi?: boolean;
}

export default function StepFolder({
  setValue,
  watch,
  spreadSheetName,
  setSpreadSheetName,
  control,
  errors,
  isNewUi = false,
}: StepFolderProps) {
  const theme = useTheme();
  const spreadsheetId = watch('spreadsheetId') || '';
  const { features } = useUserSettings();

  const FilePicker = isFeatureEnabled('drivePickerUIReact', features)
    ? SharedFilePicker
    : SharedFilePickerOld;
  const description = (
    <>
      Please select a Google Folder to use for this Bulk Operation. Make sure
      you have the appropriate files within the folder. See our{' '}
      <Link
        href={'https://promevo.com/knowledge/gpanel-support#bulk-operations'}
        id="anchor"
        target="_blank"
        rel="noreferrer noopener"
      >
        help documentation
      </Link>{' '}
      for assistance.
    </>
  );
  const handleFolderRemoved = () => {
    setValue('spreadsheetId', '');
    setSpreadSheetName('');
  };

  return (
    <Stack gap={isNewUi ? 3 : 2} width={'100%'}>
      {isNewUi ? (
        <FormSectionHeader descriptionVariant="body2" description={description}>
          Choose Folder
        </FormSectionHeader>
      ) : (
        <SectionHeader description={description}>Choose Folder</SectionHeader>
      )}
      <Stack gap={2}>
        <FilePicker
          pickerType="folder"
          selectedFileId={spreadsheetId || ''}
          selectedFileName={spreadSheetName}
          onFileSelected={({ id, name }) => {
            setValue('spreadsheetId', id);
            setSpreadSheetName(name);
          }}
          onFileRemoved={handleFolderRemoved}
          errorMessage={
            errors.spreadsheetId
              ? 'Please provide a Google Folder to use for this bulk operation.'
              : undefined
          }
          includeSharedDrives
          customTrigger={
            isNewUi
              ? (openPicker) => (
                  <PickerTriggerButton
                    noun="Folder"
                    hasSelection={Boolean(spreadsheetId)}
                    onClick={openPicker}
                  />
                )
              : undefined
          }
        />
        {isNewUi && spreadsheetId && (
          <SelectedFileRow
            name={spreadSheetName}
            icon={FolderOutlined}
            onRemove={handleFolderRemoved}
            width={454}
          />
        )}
      </Stack>
      <Box width={isNewUi ? undefined : 400}>
        <Controller
          name="setAsProcessed"
          control={control}
          render={({ field: props }) => (
            <Tooltip
              arrow
              title={
                'This option if selected will move processed images into a subfolder and leave any images that errored out'
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.checked);
                    }}
                    sx={isNewUi ? { p: 0 } : undefined}
                  />
                }
                label="Move processed images into subfolder?"
                sx={isNewUi ? { m: 0, gap: 2 } : undefined}
                slotProps={
                  isNewUi
                    ? {
                        typography: {
                          variant: 'body2',
                          color: theme.palette.semantic.text.body,
                        },
                      }
                    : undefined
                }
              />
            </Tooltip>
          )}
        />
      </Box>
    </Stack>
  );
}
